<template>
  <Message
    :severity="primeSeverity"
    :closable="!!announcement.dismissible"
    :icon="severityIcon"
    :aria-label="ariaLabel"
    class="system-announcement-banner"
    @close="$emit('dismiss', announcement.id)"
  >
    <div class="flex flex-col">
      <div class="font-bold text-base leading-tight">{{ announcement.title }}</div>
      <div
        v-if="announcement.body"
        class="system-announcement-banner__body text-sm mt-1"
        v-safe-html="announcement.body"
      ></div>
    </div>
  </Message>
</template>

<script setup>
import { computed } from 'vue';
import Message from 'primevue/message';

const props = defineProps({
  announcement: {
    type: Object,
    required: true,
  },
});

defineEmits(['dismiss']);

const SEVERITY_MAP = {
  info: { prime: 'info', icon: 'fa-solid fa-circle-info', label: 'Information' },
  warning: { prime: 'warn', icon: 'fa-solid fa-triangle-exclamation', label: 'Warning' },
  error: { prime: 'error', icon: 'fa-solid fa-circle-exclamation', label: 'Error' },
  success: { prime: 'success', icon: 'fa-solid fa-circle-check', label: 'Success' },
};

const meta = computed(() => SEVERITY_MAP[props.announcement.severity] || SEVERITY_MAP.info);

const primeSeverity = computed(() => meta.value.prime);
const severityIcon = computed(() => meta.value.icon);
const ariaLabel = computed(
  () => `${meta.value.label} announcement: ${props.announcement.title}`
);
</script>

<style scoped>
.system-announcement-banner {
  width: 100%;
  border-radius: 0;
  border-left: 0;
  border-right: 0;
  border-top: 0;
  margin: 0;
}

.system-announcement-banner :deep(.p-message-text) {
  flex: 1;
  min-width: 0;
}

.system-announcement-banner__body :deep(p) {
  margin: 0;
}
.system-announcement-banner__body :deep(p + p) {
  margin-top: 0.25rem;
}
.system-announcement-banner__body :deep(a) {
  text-decoration: underline;
  font-weight: 600;
}
.system-announcement-banner__body :deep(.ql-align-center) {
  text-align: center;
}
.system-announcement-banner__body :deep(.ql-align-right) {
  text-align: right;
}
.system-announcement-banner__body :deep(.ql-align-justify) {
  text-align: justify;
}

.system-announcement-banner__body :deep(ul) {
  list-style-type: disc;
  padding-left: 1.5rem;
  margin: 0.25rem 0;
}
.system-announcement-banner__body :deep(ol) {
  list-style-type: decimal;
  padding-left: 1.5rem;
  margin: 0.25rem 0;
}
.system-announcement-banner__body :deep(ol > li[data-list="bullet"]) {
  list-style-type: disc;
}
.system-announcement-banner__body :deep(ol > li[data-list="ordered"]) {
  list-style-type: decimal;
}

@media (prefers-reduced-motion: reduce) {
  .system-announcement-banner :deep(.p-message) {
    animation: none !important;
    transition: none !important;
  }
}
</style>
